import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface MeasuringCupVisualProps {
  maxVolume: number;
  currentVolume: number;
  targetVolume?: number;
  unit?: 'ml' | 'l' | 'cl';
  label?: string;
  mode?: 'stimulus' | 'support';
  hideValue?: boolean;
}
export function MeasuringCupVisual({
  maxVolume,
  currentVolume,
  targetVolume,
  unit = 'ml',
  label,
  mode = 'support',
  hideValue = false
}: MeasuringCupVisualProps) {
  const computed = useMemo(() => {
    const currentPercent = currentVolume / maxVolume * 100;
    const targetPercent = targetVolume !== undefined ? targetVolume / maxVolume * 100 : null;
    // Calculate scale markings (5 evenly spaced marks)
    const marks: number[] = [];
    const interval = maxVolume / 5;
    for (let i = 0; i <= 5; i++) {
      marks.push(Math.round(i * interval));
    }
    return {
      currentPercent: Math.max(0, Math.min(100, currentPercent)),
      targetPercent: targetPercent !== null ? Math.max(0, Math.min(100, targetPercent)) : null,
      marks
    };
  }, [maxVolume, currentVolume, targetVolume]);
  const cupViz = <div className="flex justify-center items-center" data-id="element-8244">
      <svg width="120" height="180" viewBox="0 0 120 180" className="drop-shadow-sm" data-id="element-8245">
        {/* Cup outline (trapezoid shape) */}
        <defs data-id="element-8246">
          <linearGradient id="glassGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-8247">
            <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.3" data-id="element-8248" />
            <stop offset="50%" stopColor="#bae6fd" stopOpacity="0.2" data-id="element-8249" />
            <stop offset="100%" stopColor="#e0f2fe" stopOpacity="0.3" data-id="element-8250" />
          </linearGradient>
          <linearGradient id="liquidGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-8251">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.7" data-id="element-8252" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.8" data-id="element-8253" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.7" data-id="element-8254" />
          </linearGradient>
        </defs>

        {/* Cup body */}
        <path d="M 30 10 L 20 160 L 100 160 L 90 10 Z" fill="url(#glassGradient)" stroke="#64748b" strokeWidth="2" data-id="element-8255" />

        {/* Scale markings */}
        {computed.marks.map((volume, i) => {
        const percent = volume / maxVolume * 100;
        const y = 160 - percent / 100 * 150;
        const leftX = 30 - i / 5 * 10;
        const rightX = 90 + i / 5 * 10;
        return <g key={volume} data-id="element-8256">
              <line x1={leftX} y1={y} x2={leftX + 10} y2={y} stroke="#475569" strokeWidth="1" data-id="element-8257" />
              <line x1={rightX - 10} y1={y} x2={rightX} y2={y} stroke="#475569" strokeWidth="1" data-id="element-8258" />
              <text x={rightX + 5} y={y + 3} fontSize="10" fill="#475569" fontWeight="600" data-id="element-8259">
                {volume}
              </text>
            </g>;
      })}

        {/* Liquid (animated) */}
        <motion.path d={`M ${20 + (160 - (160 - computed.currentPercent / 100 * 150)) * 0.066} ${160 - computed.currentPercent / 100 * 150} 
              L 20 160 
              L 100 160 
              L ${100 - (160 - (160 - computed.currentPercent / 100 * 150)) * 0.066} ${160 - computed.currentPercent / 100 * 150} 
              Z`} fill="url(#liquidGradient)" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-8260" />

        {/* Liquid surface shimmer */}
        <motion.ellipse cx="60" cy={160 - computed.currentPercent / 100 * 150} rx={40 - (160 - computed.currentPercent / 100 * 150) * 0.066} ry="3" fill="#60a5fa" opacity="0.6" data-id="element-8261" />

        {/* Target marker (if provided) */}
        {computed.targetPercent !== null && mode === 'stimulus' && <g data-id="element-8262">
            <line x1={20 + (160 - (160 - computed.targetPercent / 100 * 150)) * 0.066} y1={160 - computed.targetPercent / 100 * 150} x2={100 - (160 - (160 - computed.targetPercent / 100 * 150)) * 0.066} y2={160 - computed.targetPercent / 100 * 150} stroke="#f59e0b" strokeWidth="2" strokeDasharray="4 2" data-id="element-8263" />
            <text x="105" y={160 - computed.targetPercent / 100 * 150 + 4} fontSize="12" fill="#d97706" fontWeight="bold" data-id="element-8264">
              ?
            </text>
          </g>}

        {/* Volume label */}
        {mode === 'support' && !hideValue && <text x="60" y="175" textAnchor="middle" fontSize="12" fill="#1e293b" fontWeight="bold" data-id="element-8265">
            {currentVolume} {unit}
          </text>}
      </svg>
    </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-xs mx-auto" data-id="element-8266">
        {label && <p className="text-sm font-semibold text-slate-700 mb-2 text-center" data-id="element-8267">
            {label}
          </p>}
        {cupViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-cyan-100 overflow-hidden w-full max-w-sm mx-auto shadow-sm" data-id="element-8268">
      <div className="bg-cyan-50 px-4 py-3 border-b border-cyan-100" data-id="element-8269">
        <h3 className="text-sm font-bold text-cyan-900 text-center" data-id="element-8270">
          {label || 'Maatbeker'}
        </h3>
      </div>

      <div className="p-4 bg-white" data-id="element-8271">{cupViz}</div>

      <div className="bg-cyan-50/50 p-4 border-t border-cyan-50" data-id="element-8272">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-8273">
          Er zit nu{' '}
          <strong data-id="element-8274">
            {currentVolume} {unit}
          </strong>{' '}
          in de beker. Kijk naar de bovenkant van de blauwe vloeistof om het
          volume af te lezen.
        </p>
      </div>
    </div>;
}